import { atom } from "jotai";
import { atomFamily } from "jotai-family";
import { atomWithDefault } from "jotai/utils";
import { isEqual } from "lodash-es";
import { config } from "../../config";
import { getTokenAtom } from "../../server/auth/store";
import { authHeader } from "../../utils/auth-header";
import { loadable } from "../../utils/jotai-loadable";
import { withContentTracking } from "../content-generation/track-content-generation";
import { debugLabel, queryAtom } from "../generate/store";
import { selfDriving } from "../self-driving/self-driving";

import type { OmitUnion } from "../../utils/omit-union";
import type { ContentTone } from "../brand-kit/server/brand-kit-service";
import type { BrandTone } from "../brand-kit/server/brand-tone-service";
import type { LibraryTemplate } from "../discover/library/types";
import type { GenerateQuery } from "../generate/types";
import type {
  ContentRefineAtoms,
  ImageRefineAtoms,
  RefineHistory,
} from "../generic/atoms/types/refine";
import type { ToneType } from "../self-driving/types";

export const historyAtomFamily = atomFamily(
  ({
    contentKey,
    query,
    template,
    customTone,
  }: {
    contentKey: ToneType;
    query?: GenerateQuery | null;
    template?: LibraryTemplate | null;
    customTone?: BrandTone | null;
  }) =>
    debugLabel(
      `historyAtomFamily?contentKey=${contentKey}&slug=${template?.slug}&customTone=${customTone?.id}&query=${JSON.stringify(query)}`,
      atomWithDefault(() => [] as RefineHistory[]),
    ),
  isEqual,
);

export const refineAtomFamily = atomFamily(
  (h?: RefineHistory) =>
    debugLabel(
      `refineAtomFamily(${h?.prompt})`,
      loadable(
        atom(async (get) => {
          if (!h) return null;

          const token = await get(getTokenAtom).getToken();

          if (!token) return null;

          if (h.store === "discover" && h.type === "static") {
            const { data } = await withContentTracking(
              token,
              { contentType: "moment", subType: "discover_refine_initial", prompt: h.prompt },
              async () => {
                const { data: result, error } = await selfDriving.POST(
                  "/discover/refine/text/initial",
                  {
                    body: { ...h.extra, prompt: h.prompt },
                    params: { query: { model: config.gpt_model } },
                    ...authHeader(token),
                  },
                );
                if (error || !result) throw new Error(error.error || "Request failed");
                return result;
              },
            );
            return data;
          }

          if (h.store === "discover" && h.type === "generated") {
            const { data } = await withContentTracking(
              token,
              { contentType: "moment", subType: "discover_refine", prompt: h.prompt },
              async () => {
                const { data: result, error } = await selfDriving.POST("/discover/refine/text", {
                  body: { ...h.extra, prompt: h.prompt },
                  params: { query: { model: config.gpt_model } },
                  ...authHeader(token),
                });
                if (error || !result) throw new Error(error.error || "Request failed");
                return result;
              },
            );
            return data;
          }

          if (h.store === "generate" && h.type === "generated") {
            const q = get(queryAtom);
            if (q) {
              const { data } = await withContentTracking(
                token,
                { contentType: "moment", subType: "recipe_refine", prompt: h.prompt },
                async () => {
                  const { data: result, error } = await selfDriving.POST(
                    "/recipes/{recipeId}/follow_up/text/refine",
                    {
                      body: { ...h.extra, prompt: h.prompt },
                      params: {
                        path: { recipeId: q.recipeId },
                        query: { model: config.gpt_model },
                      },
                      ...authHeader(token),
                    },
                  );
                  if (error || !result) throw new Error(error.error || "Request failed");
                  return result;
                },
              );
              return data;
            }
          }

          return null;
        }),
      ),
    ),
  isEqual,
);

export const imageRefineAtomsAtom = atom<ImageRefineAtoms | null>(null);

export const contentRefineAtoms = atom<ContentRefineAtoms | null>(null);

export const contentRefineConfigFamily = atomFamily((tone: ContentTone | "custom") =>
  atom<OmitUnion<RefineHistory, "prompt"> | null>((get) => {
    const refineAtoms = get(contentRefineAtoms);

    if (!refineAtoms) return null;

    const content = refineAtoms.content[tone];

    if (content?.state !== "hasData" || !content.data) return null;

    return content.data.type === "static"
      ? {
          store: "discover",
          type: content.data.type,
          extra: { text: content.data.data.response.content },
        }
      : {
          store: refineAtoms.store,
          type: content.data.type,
          extra: { chatId: content.data.data.chatId },
        };
  }),
);
